/**
 * Re-pricing a whole quotation into another currency.
 *
 * The currency picker is a VIEW — it converts figures on screen and writes nothing. This is the
 * separate, deliberate act of making that currency the document's own: every selling price is
 * converted, `customer_currency` and `exchange_rate` are set, and the customer's copy is now in a
 * different currency. It changes what a customer is charged, so it is never a side effect of
 * choosing a view.
 *
 * ---- why the raw wire rows are echoed back ----
 *
 * `update_quotation` replaces the `items` child table WHOLESALE — anything not sent is deleted.
 * The domain `QuotationLine` is a lossy view of a wire row: it has no `item_name`,
 * `lowest_quote_price`, `lowest_quote_supplier` or `selected_rate`, all of which exist on the
 * server row (measured 2026-08-09). Rebuilding the payload from domain lines would therefore
 * silently strip the comparison evidence off every line.
 *
 * So the caller reads the RAW rows and passes them here untouched; each row is spread and only
 * the price fields are replaced. A field this module has never heard of survives by default,
 * which is the only safe posture against a wholesale write.
 *
 * ---- what converts, and what must not (CORRECTED 2026-09-14) ----
 *
 * `sell_unit`, `unit_price` and `amount` are what the customer pays: converted.
 *
 * `cost_unit`, `selected_rate` and `conversion_rate` are converted TOO. This module used to insist
 * the cost stayed in the company's currency as "what we are actually charged" — and measured on
 * QTN-2026-00017, that made the whole operation a no-op: the server IGNORES a written
 * `sell_unit` and derives it as `cost_unit × (1 + margin/100)`. Converting the sell alone left
 * every line's figures in the old currency under a new label, and the screen, trusting the
 * label, converted them again on every view. QTN-2026-00031 on the bench is the result — "USD"
 * with dirham figures.
 *
 * So the line is single-currency: cost, sell and selected rate all in the quotation's currency,
 * and `conversion_rate` (`po_currency` → quotation currency) scaled with them so that
 * `po_currency_rate × conversion_rate = cost_unit` still holds. What we are actually charged
 * lives on, unconverted, in `po_currency_rate` — the supplier's own figure in the supplier's
 * own currency, which is the one fact here that must never be restated.
 *
 * `margin` is NOT converted. It is a percentage, and a percentage does not change when both
 * sides of it are measured in a different unit.
 */

/** A wire quotation row, as `get_quotation` returns it — unknown fields included. */
export type WireItemRow = Record<string, unknown> & {
  qty?: number
  sell_unit?: number
  unit_price?: number
  amount?: number
  cost_unit?: number
  selected_rate?: number
  conversion_rate?: number
}

const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Every row with its customer-facing prices converted at `rate`, and nothing else touched.
 *
 * A rate of 0 or a non-finite one returns the rows UNCHANGED rather than zeroing every price —
 * the caller is expected to refuse the operation, and a defensive no-op is better than a
 * quotation silently priced at nil if one ever slips through.
 */
export function convertItems(items: readonly WireItemRow[], rate: number): WireItemRow[] {
  if (!Number.isFinite(rate) || rate <= 0) return [...items]

  return items.map((row) => {
    const sell = Number(row.sell_unit)
    const qty = Number(row.qty)
    if (!Number.isFinite(sell)) return { ...row }

    const converted = round2(sell * rate)
    const scaled = (value: unknown): number | undefined => {
      const n = Number(value)
      return Number.isFinite(n) ? round2(n * rate) : undefined
    }
    const cost = scaled(row.cost_unit)
    const selected = scaled(row.selected_rate)
    // Full precision — this is a rate, not a price, and rounding it is what breaks the identity.
    const conversion = Number.isFinite(Number(row.conversion_rate)) ? Number(row.conversion_rate) * rate : undefined
    return {
      ...row,
      sell_unit: converted,
      // Mirrors `sell_unit` on the server — measured: a single price edit writes both. Leaving it
      // behind would put two different prices on one line.
      unit_price: converted,
      amount: Number.isFinite(qty) ? round2(converted * qty) : row.amount,
      ...(cost !== undefined ? { cost_unit: cost } : {}),
      ...(selected !== undefined ? { selected_rate: selected } : {}),
      ...(conversion !== undefined ? { conversion_rate: conversion } : {}),
    }
  })
}

/** The document total implied by the converted rows — the sum the server will recompute anyway. */
export function convertedNetTotal(items: readonly WireItemRow[]): number {
  return round2(items.reduce((sum, row) => sum + (Number(row.amount) || 0), 0))
}

/**
 * The document's own FIXED amounts, restated into the new currency (Akhil, 2026-09-16).
 *
 * `convertItems` moves every line, but a quotation also carries amounts that are not lines: an
 * `Actual` tax row (a delivery charge of AED 65) and an amount-led additional discount (AED
 * 8.63). "Bill this customer in USD" left both at their AED figures, which were then read as
 * USD — the delivery charge was quoted at $65 and the discount at $8.63, and the total was wrong
 * by the difference. A percentage row keeps its rate; its amount is restated too, so the stored
 * table agrees with the lines until the next recompute. Running totals follow.
 */
export function convertFixedAmounts(
  doc: {
    additional_discount_amount?: number | null
    taxes?: ReadonlyArray<{ charge_type?: string | null; account_head?: string | null; tax_rate?: number | null; tax_amount?: number | null; row_id?: string | null; total?: number | null }>
  },
  rate: number,
): { additional_discount_amount?: number; taxes?: Array<{ charge_type: string; account_head: string; tax_rate: number; tax_amount: number; row_id: string; total: number }> } {
  const out: ReturnType<typeof convertFixedAmounts> = {}
  const discount = Number(doc.additional_discount_amount)
  if (Number.isFinite(discount) && discount > 0) out.additional_discount_amount = round2(discount * rate)
  const taxes = doc.taxes ?? []
  if (taxes.length > 0) {
    out.taxes = taxes.map((t, i) => ({
      charge_type: String(t.charge_type ?? 'Actual'),
      account_head: String(t.account_head ?? ''),
      tax_rate: Number(t.tax_rate) || 0,
      tax_amount: round2((Number(t.tax_amount) || 0) * rate),
      row_id: String(t.row_id || i + 1),
      total: round2((Number(t.total) || 0) * rate),
    }))
  }
  return out
}

/**
 * A line with no PO side recorded is "bought in the quotation's currency at the cost shown"
 * (`toQuotationLine`'s fallback) — true until the quotation changes currency, after which the
 * base price read as the NEW currency ("BASE 19.00 USD" for a cost that was AED 19). So before
 * the lines move, the original currency and cost are written as the PO side (conversion 1, which
 * `convertItems` then scales to the step), exactly as a line priced from a supplier carries them.
 */
export function pinPoSide(items: readonly WireItemRow[], fromCurrency: string): WireItemRow[] {
  return items.map((row) => {
    if (typeof row.po_currency === 'string' && row.po_currency !== '' && Number(row.po_currency_rate) > 0) return { ...row }
    const cost = Number(row.cost_unit)
    if (!Number.isFinite(cost) || cost <= 0) return { ...row }
    // `conversion_rate: 1` is the pre-conversion truth (same currency); `convertItems` scales it
    // to the step along with every other row's, so the two run in that order.
    return { ...row, po_currency: fromCurrency, po_currency_rate: cost, conversion_rate: 1 }
  })
}
